/**
 * Componentes de visualización del panel.
 *
 * Decisiones de forma, tomadas por el trabajo que hace cada dato:
 *
 *  - MAGNITUD comparada entre categorías (avance por estrato, por conglomerado,
 *    balanceo del experimento) → barras horizontales de UNA serie. Una sola
 *    serie no lleva leyenda: el título nombra lo que se mide.
 *  - POLARIDAD respecto a un punto de referencia (desviación en puntos
 *    porcentuales frente al padrón) → barras divergentes con punto medio gris y
 *    eje cero explícito. Azul por debajo, rojo por encima.
 *  - CAMBIO EN EL TIEMPO (avance diario) → columnas de una serie.
 *  - UNA SOLA CIFRA (avance total, margen de error, duración) → mosaico, no
 *    gráfica: una gráfica añadiría ejes y marcas para comunicar un número.
 *
 * Reglas que se respetan en todos: un solo eje por gráfica, marcas delgadas con
 * el extremo de dato redondeado y anclado a la base, cromo recesivo, etiqueta
 * directa selectiva en lugar de un número sobre cada marca, y capa de hover con
 * el detalle completo.
 */

import type { ReactNode } from 'react';

/* -------------------------------------------------------------------------- */
/*                                  Formato                                   */
/* -------------------------------------------------------------------------- */

export const fmtInt = (value: number): string => value.toLocaleString('es-MX');

export const fmtPct = (value: number, digits = 1): string =>
  `${(value * 100).toLocaleString('es-MX', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}%`;

export const fmtPp = (value: number, digits = 1): string =>
  `${value >= 0 ? '+' : '−'}${Math.abs(value).toLocaleString('es-MX', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })} pp`;

export const fmtDecimal = (value: number, digits = 1): string =>
  value.toLocaleString('es-MX', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

/* -------------------------------------------------------------------------- */
/*                                  Mosaico                                   */
/* -------------------------------------------------------------------------- */

export function Tile({
  label,
  value,
  unit,
  meta,
  hero,
  reference,
}: {
  label: string;
  value: ReactNode;
  unit?: string;
  meta?: ReactNode;
  hero?: boolean;
  /** Sección de la metodología que fundamenta la cifra. */
  reference?: string;
}) {
  return (
    <div className="tile">
      <p className="tile__label">
        {label}
        {reference ? <span className="ref" style={{ marginLeft: 6 }}>{reference}</span> : null}
      </p>
      <p className={`tile__value${hero ? ' hero' : ''}`}>
        {value}
        {unit ? <span className="tile__unit">{unit}</span> : null}
      </p>
      {meta ? <p className="tile__meta">{meta}</p> : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                           Barras horizontales                              */
/* -------------------------------------------------------------------------- */

export interface BarRow {
  key: string;
  label: string;
  value: number;
  /** Referencia de meta, dibujada como línea fina sobre la pista. */
  target?: number;
  /** Texto del extremo derecho. Si se omite, se muestra el valor. */
  valueLabel?: string;
  /** Contenido del tooltip. */
  tooltip?: string;
  /** Marca la fila con un color de estado en lugar del hue secuencial. */
  status?: 'good' | 'warning' | 'serious' | 'critical';
}

/**
 * Barras horizontales de una sola serie.
 *
 * La escala es común a todas las filas —incluidas las metas— para que las
 * longitudes sean comparables. Escalar cada fila a su propia meta convertiría
 * la gráfica en un conjunto de porcentajes disfrazados de barras.
 */
export function BarChart({
  rows,
  max,
  showTargetLegend,
}: {
  rows: readonly BarRow[];
  max?: number;
  showTargetLegend?: boolean;
}) {
  const scale = Math.max(
    1,
    max ?? Math.max(...rows.map((r) => Math.max(r.value, r.target ?? 0)), 1),
  );

  return (
    <>
      <div className="chart">
        {rows.map((row) => {
          const width = Math.min(100, (row.value / scale) * 100);
          const targetLeft =
            row.target !== undefined ? Math.min(100, (row.target / scale) * 100) : null;
          const color = row.status ? `var(--status-${row.status})` : 'var(--seq-450)';
          return (
            <div className="hbar tooltip" key={row.key} tabIndex={0}>
              <span className="hbar__label" title={row.label}>
                {row.label}
              </span>
              <span className="hbar__track">
                <span
                  className="hbar__mark"
                  style={{ width: `${width}%`, background: color }}
                />
                {targetLeft !== null ? (
                  <span className="hbar__target" style={{ left: `${targetLeft}%` }} />
                ) : null}
              </span>
              <span className="hbar__value">{row.valueLabel ?? fmtInt(row.value)}</span>
              {row.tooltip ? <span className="tooltip__content">{row.tooltip}</span> : null}
            </div>
          );
        })}
      </div>
      {showTargetLegend ? (
        <div className="legend">
          <span className="legend__item">
            <span className="legend__swatch" style={{ background: 'var(--seq-450)' }} />
            Levantadas
          </span>
          <span className="legend__item">
            <span
              className="legend__swatch"
              style={{ background: 'var(--ink-muted)', width: 2, height: 13, borderRadius: 1 }}
            />
            Meta
          </span>
        </div>
      ) : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*                           Barras divergentes                               */
/* -------------------------------------------------------------------------- */

export interface DivergingRow {
  key: string;
  label: string;
  /** Valor con signo. El cero es el punto medio neutro. */
  value: number;
  valueLabel?: string;
  tooltip?: string;
}

/**
 * Barras divergentes para desviación con signo.
 *
 * Azul hacia la izquierda (por debajo de lo esperado), rojo hacia la derecha
 * (por encima), gris en el medio. El par azul↔rojo se validó contra las
 * superficies de este panel en modo claro y oscuro. La escala es simétrica
 * alrededor del cero: una escala asimétrica haría parecer mayor una desviación
 * negativa que una positiva del mismo tamaño.
 */
export function DivergingChart({
  rows,
  maxAbs,
  negativeLabel = 'Por debajo del padrón',
  positiveLabel = 'Por encima del padrón',
}: {
  rows: readonly DivergingRow[];
  maxAbs?: number;
  negativeLabel?: string;
  positiveLabel?: string;
}) {
  const scale = Math.max(
    0.5,
    maxAbs ?? Math.max(...rows.map((r) => Math.abs(r.value)), 0.5),
  );

  return (
    <>
      <div className="chart">
        {rows.map((row) => {
          const halfWidth = Math.min(50, (Math.abs(row.value) / scale) * 50);
          const isNegative = row.value < 0;
          return (
            <div className="dbar tooltip" key={row.key} tabIndex={0}>
              <span className="hbar__label" title={row.label}>
                {row.label}
              </span>
              <span className="dbar__track">
                <span className="dbar__zero" />
                <span
                  className={`dbar__mark dbar__mark--${isNegative ? 'negative' : 'positive'}`}
                  style={
                    isNegative
                      ? { right: '50%', width: `${halfWidth}%` }
                      : { left: '50%', width: `${halfWidth}%` }
                  }
                />
              </span>
              <span className="hbar__value">{row.valueLabel ?? fmtPp(row.value)}</span>
              {row.tooltip ? <span className="tooltip__content">{row.tooltip}</span> : null}
            </div>
          );
        })}
      </div>
      <div className="legend">
        <span className="legend__item">
          <span className="legend__swatch" style={{ background: 'var(--div-negative)' }} />
          {negativeLabel}
        </span>
        <span className="legend__item">
          <span className="legend__swatch" style={{ background: 'var(--div-positive)' }} />
          {positiveLabel}
        </span>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*                                 Columnas                                   */
/* -------------------------------------------------------------------------- */

export interface ColumnPoint {
  key: string;
  label: string;
  value: number;
  tooltip?: string;
}

/** Columnas de una serie a lo largo del tiempo. */
export function ColumnChart({
  points,
  /** Línea de referencia horizontal, p. ej. la capacidad diaria esperada. */
  reference,
  referenceLabel,
}: {
  points: readonly ColumnPoint[];
  reference?: number;
  referenceLabel?: string;
}) {
  const max = Math.max(...points.map((p) => p.value), reference ?? 0, 1);

  return (
    <>
      <div style={{ position: 'relative' }}>
        {reference !== undefined ? (
          <div
            aria-hidden
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: `${(reference / max) * 100 * 0.84}%`,
              borderTop: '2px dashed var(--ink-muted)',
              opacity: 0.6,
              zIndex: 1,
            }}
          />
        ) : null}
        <div className="cols">
          {points.map((point) => (
            <div className="col tooltip" key={point.key} tabIndex={0}>
              <span
                className="col__mark"
                style={{ height: `${Math.max(2, (point.value / max) * 100)}%` }}
              />
              <span className="col__label">{point.label}</span>
              {point.tooltip ? <span className="tooltip__content">{point.tooltip}</span> : null}
            </div>
          ))}
        </div>
      </div>
      {referenceLabel ? (
        <div className="legend">
          <span className="legend__item">
            <span
              className="legend__swatch"
              style={{ background: 'var(--seq-450)' }}
            />
            Entrevistas completadas
          </span>
          <span className="legend__item">
            <span
              className="legend__swatch"
              style={{
                background: 'transparent',
                borderTop: '2px dashed var(--ink-muted)',
                height: 0,
                width: 14,
                borderRadius: 0,
              }}
            />
            {referenceLabel}
          </span>
        </div>
      ) : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*                                Progreso                                    */
/* -------------------------------------------------------------------------- */

/** Barra de avance sobre una meta. Para una sola magnitud contra su objetivo. */
export function ProgressBar({
  value,
  target,
  programmed,
}: {
  value: number;
  target: number;
  /** Entrevistas programadas, que exceden la meta por la merma esperada. */
  programmed?: number;
}) {
  const scale = Math.max(target, programmed ?? target, 1);
  const valueWidth = (value / scale) * 100;
  const targetLeft = (target / scale) * 100;

  return (
    <>
      <div className="hbar__track" style={{ height: 20 }}>
        <span className="hbar__mark" style={{ width: `${Math.min(100, valueWidth)}%` }} />
        {programmed !== undefined && programmed > target ? (
          <span className="hbar__target" style={{ left: `${targetLeft}%` }} />
        ) : null}
      </div>
      <div className="row row--between" style={{ marginTop: 8 }}>
        <span className="muted num">
          {fmtInt(value)} de {fmtInt(target)} ({fmtPct(target > 0 ? value / target : 0, 1)})
        </span>
        {programmed !== undefined && programmed > target ? (
          <span className="faint num">{fmtInt(programmed)} programadas</span>
        ) : null}
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*                            Indicador de estado                             */
/* -------------------------------------------------------------------------- */

const STATUS_ICONS: Record<string, string> = {
  good: '●',
  warning: '▲',
  serious: '▲',
  critical: '■',
  neutral: '○',
};

/**
 * Indicador de estado con ICONO y ETIQUETA.
 *
 * El icono no es decorativo: en superficie clara el amarillo de `warning` queda
 * por debajo de 3:1 por diseño de la paleta de estado, y la forma más el texto
 * son lo que evita que el color cargue el significado solo.
 */
export function StatusChip({
  status,
  children,
}: {
  status: 'good' | 'warning' | 'serious' | 'critical' | 'neutral' | 'accent';
  children: ReactNode;
}) {
  const icon = STATUS_ICONS[status];
  return (
    <span className={`chip chip--${status}`}>
      {icon ? (
        <span className="chip__icon" aria-hidden>
          {icon}
        </span>
      ) : null}
      {children}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*                              Vista de tabla                                */
/* -------------------------------------------------------------------------- */

/**
 * Alternativa en tabla de cualquier gráfica.
 *
 * No es un extra: garantiza que la información esté disponible sin depender del
 * color, y es el respaldo exigido cuando un color de estado queda por debajo
 * del umbral de contraste.
 */
export function TableView({
  caption,
  columns,
  rows,
}: {
  caption?: string;
  columns: readonly string[];
  rows: readonly (readonly ReactNode[])[];
}) {
  return (
    <details className="note">
      <summary>Ver como tabla</summary>
      <div className="table-wrap" style={{ marginTop: 10 }}>
        <table className="table table--numeric">
          {caption ? <caption className="faint" style={{ padding: 8, textAlign: 'left' }}>{caption}</caption> : null}
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column}>{column}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td key={j}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}
